<template>
  <ToggleCard
    v-model="model"
    :body-class="['flex pt-100', { 'pb-none': model }]"
    container-id="lottery_prepaid_subscription_form"
    :info-button-aria-label="$t('infobutton.conveniencefee.label')"
    :info-headline="$t('lottery.tickettypetoggle.prepaid.infobutton.headline')"
    :info-text="$t('lottery.tickettypetoggle.prepaid.infobutton.text')"
    name="prepaidSubscription"
    :switch-aria-label-text="$t('lottery.tickettypetoggle.prepaid.switch.label')"
    :variant="EDITOR_PREPAID_SUBSCRIPTION_VARIANT"
  >
    <template #headline>
      {{ $t('lottery.tickettypetoggle.headline') }}
    </template>

    <div class="w-full flex flex-col gap-300">
      {{ $t('lottery.tickettypetoggle.prepaid.text') }}

      <div v-if="prepaidSubscription && model" class="w-full flex flex-col align-center">
        <LcForm
          ref="form"
          v-bind="{ validationSchema, initialValues }"
          :tracking="{ containerId: 'lottery_prepaid_subscription_form' }"
        >
          <LcFormField v-slot="{ field }" name="amount">
            <LcRadioGroup v-bind="field">
              <LcRadioCard
                v-for="option in cardOptions"
                :key="option.value"
                v-bind="option"
                body-class="py-100 px-100 paragraph-regular"
              />
            </LcRadioGroup>
          </LcFormField>
          <LcFormField v-slot="{ field }" name="amount">
            <LcRadioGroup v-bind="field" layout="vertical">
              <LcRadioCard
                body-class="py-100 px-100 pr-300 paragraph-regular"
                :label="$t('lottery.tickettypetoggle.prepaid.amountcustom.radio.label')"
                :value="EDITOR_PREPAID_SUBSCRIPTION_CUSTOM_LABEL"
              >
                <template
                  v-if="field.value === EDITOR_PREPAID_SUBSCRIPTION_CUSTOM_LABEL"
                  #underneath="{ disabled }"
                >
                  <LcFormField v-slot="{ field: innerField }" name="amountCustom">
                    <LcInput
                      v-bind="innerField"
                      :class="{ 'custom-input-error': innerField.errorMessage }"
                      :disabled="disabled"
                      :label="$t('lottery.tickettypetoggle.prepaid.amountcustom.input.label')"
                      :max="`${prepaidSubscriptionCustomDrawsMax}`"
                      :no-feedback="!innerField.errorMessage"
                      :placeholder="
                        $t('lottery.tickettypetoggle.prepaid.amountcustom.input.placeholder', {
                          min: prepaidSubscriptionCustomDrawsMin,
                          max: prepaidSubscriptionCustomDrawsMax,
                        })
                      "
                      type="number"
                      @keydown="
                        (evt: KeyboardEvent) =>
                          ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()
                      "
                    />
                  </LcFormField>
                </template>
              </LcRadioCard>
            </LcRadioGroup>
          </LcFormField>
        </LcForm>
      </div>
    </div>
  </ToggleCard>
</template>

<script lang="ts" setup>
import type { ComponentExposed } from 'vue-component-type-helpers'

import * as Yup from 'yup'

import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import LcForm from '#tracking/app/component-overrides/LcForm.vue'
import LcFormField from '#tracking/app/component-overrides/LcFormField.vue'

import { EDITOR_PREPAID_SUBSCRIPTION_VARIANT } from '../constants/optionCards'
import { EDITOR_PREPAID_SUBSCRIPTION_CUSTOM_LABEL, EDITOR_TICKET_TYPE } from '../constants/options'

type Props = {
  lotteryId: LotteryId
}

const { lotteryId } = defineProps<Props>()

const {
  prepaidSubscription,
  prepaidSubscriptionCustomDrawsMax,
  prepaidSubscriptionCustomDrawsMin,
  store,
} = useEditorOptions(lotteryId)

const { t } = useI18n()

const form = useTemplateRef<
  ComponentExposed<
    typeof LcForm<{
      amount: string
      amountCustom: number
    }>
  >
>('form')

const model = computed({
  get: () => store.ticketType === EDITOR_TICKET_TYPE.PrepaidSubscription,
  set: (isActive) =>
    store.setTicketType(
      isActive ? EDITOR_TICKET_TYPE.PrepaidSubscription : EDITOR_TICKET_TYPE.Single,
    ),
})

const cardOptions = computed(() =>
  prepaidSubscription.value?.selectableDrawsOptions.map((option) => ({
    label: option.toString(),
    value: option.toString(),
  })),
)

const initialValues = computed(() => ({
  amount:
    store.prepaidSubscriptionAmount?.label ??
    prepaidSubscription.value?.selectableDrawsOptions[0].toString(),
  amountCustom:
    store.prepaidSubscriptionAmount?.label === EDITOR_PREPAID_SUBSCRIPTION_CUSTOM_LABEL
      ? store.prepaidSubscriptionAmount?.value
      : undefined,
}))

const selectedAmount = computed(() => {
  if (!model.value || !form.value?.internal || !form.value?.internal.isFieldValid('amountCustom')) {
    return null
  }

  const { amount, amountCustom } = form.value.internal.values

  const isCustom = amount === EDITOR_PREPAID_SUBSCRIPTION_CUSTOM_LABEL

  if (isCustom && isNaN(amountCustom)) return null

  return isCustom
    ? { label: amount, value: amountCustom }
    : { label: amount, value: Number(amount) }
})

const validationSchema = computed(() =>
  Yup.object().shape({
    amount: Yup.string(),
    amountCustom: Yup.number().when('amount', {
      is: EDITOR_PREPAID_SUBSCRIPTION_CUSTOM_LABEL,
      otherwise: (schema) => schema.notRequired(),
      then: (schema) =>
        schema
          .required(t('lottery.tickettypetoggle.prepaid.amountcustom.input.error.required'))
          .transform((value) => (isNaN(value) ? undefined : value))
          .min(
            prepaidSubscriptionCustomDrawsMin.value,
            t('lottery.tickettypetoggle.prepaid.amountcustom.input.error.min', {
              min: prepaidSubscriptionCustomDrawsMin.value,
            }),
          )
          .max(
            prepaidSubscriptionCustomDrawsMax.value,
            t('lottery.tickettypetoggle.prepaid.amountcustom.input.error.max', {
              max: prepaidSubscriptionCustomDrawsMax.value,
            }),
          ),
    }),
  }),
)

watch(selectedAmount, (value) => {
  store.setPrepaidSubscriptionAmount(value)
})

const validate = async () => {
  if (!model.value) return true

  const validationResult = await form.value?.internal?.validate()
  if (!validationResult) return false
  return validationResult.valid
}

defineExpose({ validate })
</script>

<style lang="scss" scoped>
/* stylelint-disable */
:deep(.lc-radio-group--horizontal .lc-radio-group__wrap) {
  grid-template-columns: repeat(auto-fit, minmax(4rem, 1fr));
}

:deep(.lc-radio__label) {
  margin-left: 0.5rem;
}

:deep(.lc-feedback-message) {
  margin-block: 0.0625rem;
}

.custom-input-error :deep(.lc-feedback-message) {
  margin-top: 0.25rem;
  margin-bottom: 0;
}
</style>
